import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router";
import {
  LayoutDashboard,
  Upload,
  Music,
  Layers,
  Grid3X3,
  BarChart3,
  ScrollText,
  Webhook,
  Settings,
  Wrench,
  HardDrive,
  ArrowLeft,
  User,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";

const navItems = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/admin/upload", icon: Upload, label: "Upload" },
  { to: "/admin/sounds", icon: Music, label: "Sounds" },
  { to: "/admin/combos", icon: Layers, label: "Combos" },
  { to: "/admin/boards", icon: Grid3X3, label: "Boards" },
  { to: "/admin/analytics", icon: BarChart3, label: "Analytics" },
  { to: "/admin/audit", icon: ScrollText, label: "Audit Log" },
  { to: "/admin/webhooks", icon: Webhook, label: "Webhooks" },
  { to: "/admin/backups", icon: HardDrive, label: "Backups" },
  { to: "/admin/maintenance", icon: Wrench, label: "Maintenance" },
  { to: "/admin/settings", icon: Settings, label: "Settings" },
];

export function AdminLayout() {
  const [identity, setIdentity] = useState<{ email: string | null; name: string | null }>({ email: null, name: null });

  useEffect(() => {
    api.admin.me().then(setIdentity).catch(() => {});
  }, []);

  return (
    <div className="flex min-h-screen">
      <aside className="sticky top-0 flex h-screen w-56 flex-col border-r border-mist bg-slate">
        <div className="flex items-center gap-2.5 border-b border-mist p-4">
          <img
            src="/brand/wizard-sounds-mark.svg"
            alt=""
            className="h-10 w-10"
          />
          <span className="font-display text-xl font-semibold tracking-display text-parchment">
            admin
          </span>
        </div>

        <nav className="flex-1 p-2">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm transition-colors",
                  isActive
                    ? "bg-spark/10 text-spark font-medium"
                    : "text-fog hover:bg-slate-raised hover:text-parchment",
                )
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="space-y-1 border-t border-mist p-2">
          {identity.email && (
            <div className="flex items-center gap-2 rounded-xl px-3 py-2">
              <User className="h-4 w-4 shrink-0 text-spark" />
              <div className="min-w-0">
                {identity.name && (
                  <p className="truncate text-xs font-medium text-parchment">{identity.name}</p>
                )}
                <p className="truncate text-[10px] text-fog" title={identity.email}>
                  {identity.email}
                </p>
              </div>
            </div>
          )}
          <NavLink
            to="/"
            className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-fog hover:bg-slate-raised hover:text-parchment"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to soundboard
          </NavLink>
        </div>
      </aside>

      <main className="flex-1 p-6">
        <Outlet />
      </main>
    </div>
  );
}
